'use client'

import { useState, useMemo } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import dayjs from 'dayjs'
import { useSearchParams } from 'next/navigation'
import Link from 'next/link'

import {
  faBoxArchive,
  faFolder,
  faHome,
  faShare,
  faToolbox,
  faUserGroup,
  faFileLines,
  faClockRotateLeft,
} from '@document-crunch/pro-regular-svg-icons'

import { DocumentSearchResult } from '@dc-shared-web/hooks/api/document'
import { PlaybookListResponse } from '@dc-shared-web/hooks/api/cl-cs/types'

import { Icon, IconColor } from '@dc-shared-web/components/base/Icon'
import { Tooltip } from '@dc-shared-web/components/base/Tooltip'

import { Badge } from '@dc-shared-web/components/shadcn/badge'

import { escapeHTMLAndHighlightSearchTerm } from '@dc-shared-web/lib/helpers'

import { statusMap } from '@/app/core/utils/maps'

import SearchTableActionMenu from '@/app/search/SearchTableActionMenu'
import { ShareActionType, ShareDialog, User } from '@/dc-shared-web/src'
import { getBreadcrumbItems } from '@/app/helpers/getBreadcrumbs'
import { useGetProjectByUuid } from '@/dc-shared-web/src/hooks/api/project/useGetProjectByUuid'
import { useGetFolderById } from '@/dc-shared-web/src/hooks/api/folder/useGetFolderByUuid'
import type { ProjectNew } from '@/dc-shared-web/src/hooks/api/project'
import type { FolderBreadcrumb } from '@/dc-shared-web/src/hooks/api/folder/types'
import { useLDFlags } from '@/app/core/hooks/useLDFlags'
import { getUserVCPermissions } from '@/app/crunches/[id]/InfoPanel/utils'
import { getUploadVersionDisabledState } from '@/app/helpers/versionComparisonHelper'

export const getCrunchViewLink = (
  document: DocumentSearchResult,
  options?: { folderId?: string }
) => {
  const folderParam = options?.folderId ? `&folderId=${options.folderId}` : ''

  return `/crunches/${document.uuid}?infoPanel=Home${folderParam}`
}

export const ShareCountBadge = ({
  document,
}: {
  document: DocumentSearchResult
}) => {
  const [dialogOpen, setDialogOpen] = useState(false)

  const tooltipContent = (
    <>
      {document.sharedWith
        ?.split(',')
        .map((user) => <div key={user}>{user}</div>)}
    </>
  )

  const onShareSuccess = (action: ShareActionType) => {
    if (action === 'SHARE') {
      setDialogOpen(false)
    }
  }

  return (
    <>
      <Tooltip content={tooltipContent}>
        <button
          data-testid={`shared-users-tooltip`}
          onClick={() => setDialogOpen(true)}
          className='tw-flex tw-gap-1 tw-items-center tw-bg-neutral-100 tw-rounded-lg tw-py-0.5 tw-px-2'
        >
          <Icon icon={faUserGroup} color={IconColor.muted} size='xs' />
          <div className='tw-text-sm tw-text-neutral-300'>
            {document.shareCount}
          </div>
        </button>
      </Tooltip>
      {dialogOpen && (
        <ShareDialog
          document={{
            id: document.uuid,
            teamId: document.teamUUID,
            team: document.teamName,
            name: document.fileName,
          }}
          open={dialogOpen}
          setOpen={setDialogOpen}
          onActionSuccess={onShareSuccess}
        />
      )}
    </>
  )
}

const Type = ({
  document,
  playbookData,
}: {
  document: DocumentSearchResult
  playbookData?: PlaybookListResponse
}) => {
  const { flags } = useLDFlags()

  const hasPlaybook = playbookData?.results?.some(
    (playbook) => playbook.crunch_uuid === document.uuid
  )

  const renderTypeWithPlaybook = (typeContent: React.ReactNode) => (
    <div className='tw-flex tw-items-start tw-gap-1'>
      {typeContent}
      {hasPlaybook && (
        <>
          <span className='tw-text-neutral-300 tw-text-sm'>•</span>
          <span className='tw-text-neutral-300 tw-text-sm tw-whitespace-nowrap'>
            Playbook
          </span>
        </>
      )}
    </div>
  )

  if (!flags.pd2UploadFlow10) {
    const typeContent = (
      <div
        className='tw-text-neutral-300 tw-text-sm tw-line-clamp-1'
        data-testid='crunch-solution-type'
      >
        {document.type}
      </div>
    )
    return hasPlaybook ? renderTypeWithPlaybook(typeContent) : typeContent
  }

  if (document.documentTypeName) {
    const typeContent = (
      <div
        className='tw-text-neutral-300 tw-text-sm tw-line-clamp-1'
        data-testid='crunch-solution-type'
      >
        {document.documentTypeName}
      </div>
    )
    return hasPlaybook ? renderTypeWithPlaybook(typeContent) : typeContent
  }

  return (
    <Tooltip
      content={
        <>
          <p>{`'Solution Type' is no longer supported.`}</p>
          <p>{`All new crunches will use 'Document Type'.`}</p>
        </>
      }
      withArrow
    >
      <div
        className='tw-text-neutral-300 tw-text-sm tw-italic'
        data-testid='crunch-solution-type'
      >
        {document.type}*
      </div>
    </Tooltip>
  )
}

export const DocumentCellHelper = ({
  document,
  folderId,
  showShareInfo,
  playbookData,
}: {
  document: DocumentSearchResult
  folderId?: string
  showShareInfo: boolean
  playbookData?: PlaybookListResponse
}) => {
  const searchParams = useSearchParams()
  const searchTerm = searchParams.get('search') ?? ''
  const highlightedText = escapeHTMLAndHighlightSearchTerm(
    document.fileName,
    searchTerm
  )

  const showVersionComparison =
    document.versions &&
    document.versions.length > 0 &&
    document.status === 'completed'

  const link = getCrunchViewLink(
    document,
    /* folderId prop is populated in the folderDetails view. The document.folderId is populated in the search results table */
    folderId || document.folderId
      ? { folderId: `${folderId || document.folderId}` }
      : undefined
  )

  return (
    <div className='tw-flex tw-gap-2'>
      <Icon icon={faFileLines} size='1x' className='tw-mt-1' />
      <div>
        <div className='tw-flex tw-gap-2 tw-items-start tw-flex-wrap'>
          <div>
            <Link
              className='tw-text-blue-600'
              data-testid={`crunch-link-${document._shortenedUuid}`}
              href={link}
            >
              {highlightedText}
            </Link>
            <Type document={document} playbookData={playbookData} />
          </div>
          {showShareInfo && <ShareCountBadge document={document} />}
          {showVersionComparison && (
            <Badge
              data-testid='crunch-versions'
              size='sm'
              className='tw-whitespace-nowrap'
              variant='soft'
            >
              <Icon icon={faClockRotateLeft} size='xs' />
              Multiple versions
            </Badge>
          )}
          {document.status && document.status !== 'completed' && (
            <Badge
              data-testid='crunch-status'
              variant='soft'
              color={statusMap[document.status]?.color}
              size='sm'
            >
              {statusMap[document.status]?.text}
            </Badge>
          )}
        </div>
      </div>
    </div>
  )
}

export const CrunchedByCellHelper = ({
  document,
}: {
  document: DocumentSearchResult
}) => {
  const searchParams = useSearchParams()
  const searchTerm = searchParams.get('search') ?? ''
  const highlightedText = escapeHTMLAndHighlightSearchTerm(
    document.crunchedBy,
    searchTerm
  )

  return (
    <>
      <p data-testid='crunch-user-name'>{highlightedText}</p>
      <p
        className='tw-text-neutral-300 tw-text-sm'
        data-testid='crunch-user-email'
      >
        {document.user}
      </p>
    </>
  )
}

export const TeamCellHelper = ({
  document,
}: {
  document: DocumentSearchResult
}) => {
  return (
    <Link
      data-testid='crunch-team'
      href={`/dash?selectedTeamId=${document.teamUUID}`}
      className='tw-text-blue-600'
    >
      {document.teamName}
    </Link>
  )
}

const getLocationName = (document: DocumentSearchResult) => {
  if (document.teamId === 0) return 'Shared with me'
  if (document.isArchived) return 'Archived'
  return null
}

const getBreadcrumbPath = (
  document: DocumentSearchResult,
  projectData: ProjectNew | undefined,
  folderData: FolderBreadcrumb | undefined,
  searchParams: URLSearchParams,
  currentLocationName: string,
  isError: boolean
) => {
  const locationName = getLocationName(document)
  if (locationName) return [locationName]
  if (!document.projectUuid && !document.folderUuid) return ['Dashboard']
  if (isError) return ['Error loading path']
  if (
    (document.projectUuid && !projectData) ||
    (document.folderUuid && !folderData)
  )
    return ['Loading...']

  const items = getBreadcrumbItems(
    document.projectUuid ? projectData : folderData,
    searchParams
  ).map((item) => item.label)

  return ['Dashboard', ...items, currentLocationName]
}

const BreadcrumbTooltip = ({
  document,
}: {
  document: DocumentSearchResult
}) => {
  const searchParams = useSearchParams()
  const { name } = locationInfo(document)

  const { data: projectData, isError: isProjectError } = useGetProjectByUuid(
    document.projectUuid || ''
  )
  const { data: folderData, isError: isFolderError } = useGetFolderById(
    document.teamUUID || '',
    document.folderUuid || ''
  )

  const breadcrumbItems = useMemo(
    () =>
      getBreadcrumbPath(
        document,
        projectData,
        folderData,
        searchParams,
        name,
        isProjectError || isFolderError
      ),
    [
      document,
      projectData,
      folderData,
      searchParams,
      name,
      isProjectError,
      isFolderError,
    ]
  )

  return (
    <div className='tw-max-w-[200px] tw-p-1'>{breadcrumbItems.join(' / ')}</div>
  )
}

export const LocationCellHelper = ({
  document,
}: {
  document: DocumentSearchResult
}) => {
  const { name, icon, linkDestination } = locationInfo(document)
  const searchParams = useSearchParams()
  const searchTerm = searchParams.get('search') ?? ''
  const highlightedName = escapeHTMLAndHighlightSearchTerm(name, searchTerm)

  return (
    <Tooltip content={<BreadcrumbTooltip document={document} />}>
      <Link
        href={linkDestination}
        className='tw-flex tw-gap-2 tw-text-blue-600'
        data-testid='crunch-location'
      >
        <Icon
          icon={icon}
          color={IconColor.tertiary}
          size='sm'
          className='tw-mt-1'
        />
        <span>{highlightedName}</span>
      </Link>
    </Tooltip>
  )
}

export const locationInfo = (document: DocumentSearchResult) => {
  const optionalSelectedTeamIdParam = document.teamUUID
    ? `?selectedTeamId=${document.teamUUID}`
    : ''

  let name
  let icon
  let linkDestination

  if (document.teamId === 0) {
    name = 'Shared with me'
    icon = faShare
    linkDestination = `/shared${optionalSelectedTeamIdParam}`
  } else if (document.folderId) {
    name = document.folderName
    icon = faFolder
    linkDestination = `/folders/${document.folderUuid}${optionalSelectedTeamIdParam}`
  } else if (document.projectId) {
    name = document.projectName
    icon = faToolbox
    linkDestination = `/projects/${document.projectUuid}${optionalSelectedTeamIdParam}`
  } else if (document.isArchived) {
    name = 'Archived'
    icon = faBoxArchive
    linkDestination = `/archived${optionalSelectedTeamIdParam}`
  } else {
    name = 'Dashboard'
    icon = faHome
    linkDestination = `/dash${optionalSelectedTeamIdParam}`
  }

  return { name, icon, linkDestination }
}

export const canSeeActionMenu = (
  user: User,
  document: DocumentSearchResult
) => {
  return (
    !document.sharedWith
      ?.toLocaleLowerCase()
      ?.includes(user.email.toLocaleLowerCase()) || user.is_super_admin
  )
}

export const getSearchColumns = (
  user: User,
  params?: {
    teamHasNoFoldersNorProjects: boolean
    playbookData?: PlaybookListResponse
    versionCompareDiscoverabilityImprovements: boolean
  }
): ColumnDef<DocumentSearchResult>[] => {
  return [
    {
      accessorKey: 'fileName',
      header: 'Crunch name',
      cell: ({ row }) => {
        const showShareInfo =
          row.original.shareCount > 0 && !user._isUnaffiliatedUser

        return (
          <DocumentCellHelper
            document={row.original}
            showShareInfo={showShareInfo}
            playbookData={params?.playbookData}
          />
        )
      },
      meta: {
        widthClassName: 'tw-w-[32%]',
      },
    },
    {
      accessorKey: 'crunchedBy',
      header: 'Crunched By',
      cell: ({ row }) => <CrunchedByCellHelper document={row.original} />,
    },
    {
      accessorKey: 'teamName',
      header: 'Team',
      enableSorting: false,
      cell: ({ row }) => <TeamCellHelper document={row.original} />,
    },
    {
      accessorKey: 'location',
      header: 'Location',
      enableSorting: false,
      cell: ({ row }) => <LocationCellHelper document={row.original} />,
    },
    {
      accessorKey: 'createdAt',
      header: 'Crunch date',
      cell: ({ row }) => (
        <div data-testid='crunch-date'>
          {dayjs(row.original.createdAt).format('MMM D, YYYY h:mm A')}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => {
        if (!canSeeActionMenu(user, row.original)) {
          return null
        }

        const hasPlaybook = Boolean(
          params?.playbookData?.results?.some(
            (playbook) => playbook.crunch_uuid === row.original.uuid
          )
        )

        const versionsLength = row.original.versions?.length || 0
        const hasMoreThan1Version = versionsLength > 0

        const hasVCPermissions = getUserVCPermissions({
          user,
          hasVersionComparison: hasMoreThan1Version,
          teamId: row.original.teamId.toString(),
        })

        const showUploadVersion =
          !!params?.versionCompareDiscoverabilityImprovements &&
          hasVCPermissions

        const { disabled, tooltip } = getUploadVersionDisabledState({
          status: row.original.status,
          versionsLength,
          hasPlaybook,
        })

        return (
          <div className='tw-text-center'>
            <SearchTableActionMenu
              document={row.original}
              canMoveCrunches={!!params?.teamHasNoFoldersNorProjects}
              showUploadVersion={showUploadVersion}
              uploadVersionDisabled={disabled}
              uploadVersionTooltip={tooltip}
            />
          </div>
        )
      },
      meta: {
        widthClassName: 'tw-w-24',
      },
    },
  ]
}
